/**
 * 통화 리스트. GET /api/calls. SPEC §4 안드로이드 화면 "통화 리스트/상세".
 */
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { type CallListItem } from '../lib/api';
import { listCallsFromRoom } from '../lib/roomData';
import { EmptyState, PageHeader } from '../components/AppShell';
import {
  callStatusLabel,
  categoryLabel,
  durationLabel,
  phoneOrFallback,
  shortWhen,
} from '../lib/format';

export function Calls() {
  const { data, isLoading, isError, error } = useQuery({
    queryKey: ['calls'],
    queryFn: () => listCallsFromRoom(),
    retry: false,
  });

  return (
    <>
      <PageHeader title="통화" subtitle="걸려온 통화를 자동으로 정리해 드려요." />
      <main className="px-xl">
        {isLoading && (
          <p className="text-[13px] text-[var(--text-muted)] py-md">로딩 중…</p>
        )}
        {isError && (
          <p className="text-[13px] text-[var(--error)] py-md">
            불러오기 실패: {error instanceof Error ? error.message : String(error)}
          </p>
        )}
        {data && data.items.length === 0 && (
          <EmptyState>통화가 쌓이면 여기에 자동으로 정리됩니다.</EmptyState>
        )}
        {data && data.items.length > 0 && (
          <ul className="space-y-sm">
            {data.items.map((item) => (
              <CallRow key={item.id} item={item} />
            ))}
          </ul>
        )}
      </main>
    </>
  );
}

function CallRow({ item }: { item: CallListItem }) {
  const cat = categoryLabel(item.category);
  const status = callStatusLabel(item.status);
  const isReady = item.status === 'ready';
  return (
    <li>
      <Link
        to={`/calls/${item.id}`}
        className="block bg-[var(--surface)] border border-[var(--border)] rounded-xl shadow-butler-md hover:bg-[var(--surface-elev)] transition-colors"
      >
        <div className="px-lg py-md flex items-center gap-md">
          <div className="w-8 h-8 rounded-full bg-[var(--surface-elev)] border border-[var(--border)] grid place-items-center text-[14px] text-[var(--text-secondary)] shrink-0">
            ☎
          </div>
          <div className="min-w-0 flex-1">
            <div className="flex items-center gap-sm">
              <span className="text-[14px] font-medium text-[var(--text-primary)] truncate">
                {phoneOrFallback(item.contact_name, item.contact_phone)}
              </span>
              {cat && (
                <span className="text-[10px] px-sm py-[2px] rounded-full bg-[var(--surface-elev)] text-[var(--text-secondary)] border border-[var(--border)] shrink-0">
                  {cat}
                </span>
              )}
            </div>
            <div className="text-[12px] text-[var(--text-muted)] mt-[2px] flex items-center gap-sm">
              <span className="font-mono">{shortWhen(item.started_at)}</span>
              <span>·</span>
              <span className="font-mono">{durationLabel(item.duration_seconds)}</span>
              {!isReady && (
                <>
                  <span>·</span>
                  <span className="text-[var(--accent)]">{status}</span>
                </>
              )}
            </div>
          </div>
        </div>
      </Link>
    </li>
  );
}
